import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import {
  createCategory,
  findCategoryById,
  updateCategoryById,
} from "../../../../api/inventoryService";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  allowedReleaseKinds,
  CATEGORY_RELEASE_KINDS,
  CategoryReleaseKind,
  toggleReleaseKind,
} from "../categoryReleaseTypes";

interface AddCategoryProps {
  closeModal: () => void;
  id?: string;
}

interface FormData {
  categoryName: string;
}

const AddCategory: React.FC<AddCategoryProps> = ({ closeModal, id }) => {
  const methods = useForm<FormData>();
  // New categories start with every release ticked
  const [releaseTypes, setReleaseTypes] = useState<CategoryReleaseKind[]>(allowedReleaseKinds(null));

  const getCategoryData = useCallback(
    (categoryId: string) => {
      findCategoryById(categoryId)
        .then((data) => {
          methods.reset(data as FormData);
          setReleaseTypes(allowedReleaseKinds(data as any));
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [methods]
  );

  useEffect(() => {
    if (id) {
      getCategoryData(id);
    }
  }, [id, getCategoryData]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      const body = { categoryName: data.categoryName, releaseTypes };
      const apiCall = id
        ? updateCategoryById(id, body)
        : createCategory(body);

      apiCall
        .then(() => closeModal())
        .catch((error) => console.error(error?.response));
    },
    [closeModal, id, releaseTypes]
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-100 dark:bg-emerald-900/40 rounded-md flex items-center justify-center">
              <CategoryIcon className="text-emerald-600 dark:text-emerald-300" sx={{ fontSize: 14 }} />
            </div>
            <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">
              {id ? "Edit Category" : "Add Category"}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="p-3">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                {...methods.register("categoryName", { required: true })}
                type="text"
                placeholder="Enter category name"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>

            <div className="px-3 pb-3">
              <p className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-0.5">Can be released in</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-2">
                Products in this category only show up in the releases ticked here.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {CATEGORY_RELEASE_KINDS.map((kind) => {
                  const checked = releaseTypes.includes(kind.value);
                  return (
                    <label
                      key={kind.value}
                      className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? "border-primary/40 bg-primary/5 dark:bg-primary/20"
                          : "border-slate-200 dark:border-slate-600"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={checked}
                        onChange={() => setReleaseTypes((prev) => toggleReleaseKind(prev, kind.value))}
                      />
                      <span className={checked ? "text-primary dark:text-sky-300" : "text-slate-400"}>
                        {checked ? <CheckBoxIcon sx={{ fontSize: 18 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                      </span>
                      <span>
                        <span className="block text-xs font-medium text-slate-800 dark:text-slate-100">{kind.label}</span>
                        <span className="block text-[10px] text-slate-500 dark:text-slate-400">{kind.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {releaseTypes.length === 0 && (
                <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                  Nothing ticked: this category&apos;s products can&apos;t be released anywhere.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-600 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2.5 text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors font-medium text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
              >
                {id ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddCategory;
